// src/MobileNav.js
// Mobile-only navigation shell: a fixed top bar with a hamburger that slides
// the existing SideNav in as a drawer. Renders NOTHING at md and up — the
// desktop sidebar is completely untouched by this component.
import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Upload } from 'lucide-react';
import SideNav from './SideNav';
import GlobalReceiptScanner from './GlobalReceiptScanner';

function MobileNav() {
  const [open, setOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [hasPendingScan, setHasPendingScan] = useState(!!localStorage.getItem('adminScanPendingId'));
  const location = useLocation();
  const isSuperAdmin = localStorage.getItem('role') === 'superadmin';

  // The scanner flags an in-flight batch in localStorage; reflect it so the icon
  // pulses (and reopening resumes that batch).
  useEffect(() => {
    const sync = () => setHasPendingScan(!!localStorage.getItem('adminScanPendingId'));
    window.addEventListener('adminScan:changed', sync);
    return () => window.removeEventListener('adminScan:changed', sync);
  }, []);

  // Navigating anywhere closes the drawer.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // No body scroll behind the open drawer.
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  return (
    <div className="md:hidden">
      {/* Top bar */}
      <header
        className="fixed top-0 left-0 right-0 h-14 z-40 bg-zinc-950/95 backdrop-blur border-b border-zinc-800/60 flex items-center justify-between px-2"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <button
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          className="w-11 h-11 flex items-center justify-center text-zinc-300 active:text-white"
        >
          <Menu size={24} />
        </button>
        <NavLink to="/" className="text-white font-bold text-base">
          Stalwart Electric
        </NavLink>
        {/* Super-admin: global receipt scanner. Others get a balancing spacer.
            Pulses while a scan is pending so it can be resumed. */}
        {isSuperAdmin ? (
          <button
            onClick={() => setScanOpen(true)}
            aria-label="Scan receipt"
            className={`relative w-11 h-11 flex items-center justify-center active:text-white ${hasPendingScan ? 'text-indigo-300 animate-pulse' : 'text-indigo-300'}`}
          >
            <Upload size={22} />
            {hasPendingScan && (
              <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-indigo-400 ring-2 ring-zinc-950 animate-ping" />
            )}
          </button>
        ) : (
          <div className="w-11 h-11" />
        )}
      </header>

      {scanOpen && (
        <GlobalReceiptScanner
          initialPendingId={localStorage.getItem('adminScanPendingId') || null}
          onClose={() => setScanOpen(false)}
        />
      )}

      {/* Drawer: the real SideNav, slid in over a backdrop */}
      {open && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full">
            <SideNav />
          </div>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="absolute top-3 left-[19rem] w-11 h-11 flex items-center justify-center rounded-full bg-zinc-900/90 text-zinc-300 border border-zinc-700/60"
          >
            <X size={20} />
          </button>
        </div>
      )}
    </div>
  );
}

export default MobileNav;
